import { Currencies, GREEN, HIGHLIGHT_RED } from "@finway-group/shared/lib/consts"
import { CurrencyEnum } from "@finway-group/shared/lib/models"
import { UserLanguageEnum } from "@finway-group/shared/lib/models/user/userSettings.interface"
import React, { useEffect } from "react"
import { NumberFormatValues, NumericFormat, SourceInfo } from "react-number-format"
import { useEmployeeLanguage } from "~Shared/hooks/employee.hooks"
import { useStateIfMounted } from "~Shared/utils/hooks/useStateIfMounted"
import { CurrencySymbolPosition } from "./config"

interface CurrencyInputProps {
    id?: string
    currency?: CurrencyEnum
    value: number
    isAutoFocused?: boolean
    isAutoSelected?: boolean
    className?: string
    onChange?: (value: number) => void
    onBlur?: () => void
    colored?: boolean
    isDisabled?: boolean
    isNegativeAllowed?: boolean
    currencySymbolPosition?: CurrencySymbolPosition
    onKeyDown?: () => void
    precision?: number
}

export const CurrencyInput = ({
    currency = CurrencyEnum.USD,
    value,
    isAutoFocused = false,
    className = "react-currency-input",
    onChange,
    onBlur,
    colored,
    isDisabled = false,
    isNegativeAllowed = true,
    currencySymbolPosition = CurrencySymbolPosition.OUTSIDE_INPUT,
    onKeyDown,
    precision,
}: CurrencyInputProps) => {
    const language = useEmployeeLanguage()
    const [internalValue, setInternalValue] = useStateIfMounted<string | number>(value)
    const [decimalSeparator, thousandSeparator] = language === UserLanguageEnum.DE ? [",", "."] : [".", ","]

    const currencyData = Currencies[currency]

    useEffect(() => {
        setInternalValue(value)
    }, [value]) // DO NOT add setInternalValue as dependency! It will break the internal state logic

    const handleFocus = () => {
        if (!internalValue) {
            setInternalValue("")
        }
    }

    const handleBlur = (e: any) => {
        if (!e.target.value) {
            /**
             * this is required to resolve a library caused bug.
             *
             * If you remove the input 2 times in a row, the `formatValue` will get out of sync and the library
             * thinks its showing the correct value, even-though it does not. By checking the `internalValue` and
             * triggering a re-render the correct value is displayed
             */
            setInternalValue(internalValue === "0" ? 0 : "0")
        }

        onBlur?.()
    }

    const handleValueChange = ({ floatValue }: NumberFormatValues, { source }: SourceInfo) => {
        // In order to prevent cascading updates, the change handler should handle the user input change.
        // e.g. There are cases value is updated by other components (change source === 'prop'), it should not trigger this change handler.
        // Source tells whether the reason for this function being triggered was an 'event' or due to a 'prop' change.
        // ref: https://github.com/s-yadav/react-number-format/blob/master/documentation/v4.md#accessing-event-and-the-source-for-onvaluechangetrigger
        switch (source) {
            case "event":
                onChange?.(floatValue ?? 0)
                break
            case "prop":
            default:
                break
        }
    }

    const onColoredStyle = value > 0 ? { color: GREEN } : value < 0 ? { color: HIGHLIGHT_RED } : {}
    const placeholder = currencySymbolPosition === CurrencySymbolPosition.INSIDE_INPUT ? `${Currencies[currency]?.symbol} 0${decimalSeparator}00` : `0${decimalSeparator}00`

    return (
        <div className="flex flex-row items-center">
            {currencySymbolPosition === CurrencySymbolPosition.OUTSIDE_INPUT && Currencies[currency]?.symbol}
            <div className="overflow-hidden">
                <NumericFormat
                    prefix={currencySymbolPosition === CurrencySymbolPosition.INSIDE_INPUT ? `${Currencies[currency]?.symbol} ` : undefined}
                    placeholder={placeholder}
                    allowLeadingZeros={false}
                    disabled={isDisabled}
                    autoFocus={isAutoFocused}
                    style={colored ? onColoredStyle : {}}
                    allowNegative={isNegativeAllowed}
                    className={className}
                    decimalScale={precision ?? currencyData.decimal_digits}
                    fixedDecimalScale={!precision}
                    value={internalValue}
                    onFocus={handleFocus}
                    decimalSeparator={decimalSeparator}
                    thousandSeparator={thousandSeparator}
                    allowedDecimalSeparators={[decimalSeparator]}
                    onBlur={handleBlur}
                    onValueChange={handleValueChange}
                    onKeyDown={onKeyDown}
                    data-testid="currencyValue"
                />
            </div>
        </div>
    )
}
